import { useRef } from 'react'
import { useRouter } from 'next/router'
import { PATH_PAGE } from '@/routes/paths'
// @mui
import { Grid, Container, Stack, Typography, CircularProgress, Button } from '@mui/material'
import { styled } from '@mui/material/styles'
// hooks
import useSettings from '@/hooks/useSettings'
import useLocales from '@/hooks/useLocales'
import useApp from '@/hooks/useApp'
// @types
import { Post } from '@/types/post'
// layouts
import Layout from '@/layouts/index'
// components
import Page from '@/components/Page'
import { SkeletonPostItem } from '@/components/skeleton'
// sections
import { BlogPostCard } from '@/sections/blog'
import { postService, extractPostEntries } from '@/services/post'
import { settingService, GlobalSetting } from '@/services/page'
import pathService from '@/services/path'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useFetchAllPostsWithTitleLazyQuery, BlogsOrder } from '@/generated/graphql'
import { formatLocale } from '@/utils/contentful'
import type { GetStaticPaths, GetStaticProps } from 'next'
import { APPS } from '@/appConfig'
import type { ParsedUrlQuery } from 'querystring'

interface PathProps extends ParsedUrlQuery {
  appId: string
}

interface PostsPageProps {
  preview: boolean
  firstViewPosts: Post[]
  totalPosts: number
  allCategories: string[]
  globalSetting?: GlobalSetting
}

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
  },
}))

// ----------------------------------------------------------------------

Posts.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout variant="main">{page}</Layout>
}

// ----------------------------------------------------------------------
const POSTS_PER_PAGE = 12

export default function Posts({
  firstViewPosts = [],
  totalPosts = 0,
  allCategories = [],
}: PostsPageProps) {
  const { themeStretch } = useSettings()
  const { push, locale } = useRouter()
  const { title: rootTitle, logo, host, locales: appLocales } = useApp()
  const { translate } = useLocales()
  const isFirstRun = useRef(true)

  const [
    fetchAllPostsWithTitle,
    {
      data: visitorFetchAllPostsWithTitleQueryData,
      fetchMore: visitorFetchAllPostsWithTitleQueryFetchMore,
      loading: visitorFetchAllPostsWithTitleQueryLoading,
    },
  ] = useFetchAllPostsWithTitleLazyQuery({
    variables: {
      limit: POSTS_PER_PAGE,
      skip: POSTS_PER_PAGE,
      locale: formatLocale(locale),
      order: [BlogsOrder.SysPublishedAtDesc],
      allowedLocales: appLocales.map((appLocale) => formatLocale(appLocale) ?? appLocale),
    },
  })

  const allPost = [
    ...firstViewPosts,
    ...extractPostEntries(visitorFetchAllPostsWithTitleQueryData?.blogsCollection),
  ]

  const handleFetchMore = () => {
    if (visitorFetchAllPostsWithTitleQueryLoading) {
      return
    }
    if (isFirstRun.current) {
      fetchAllPostsWithTitle()
      isFirstRun.current = false
      return
    }
    visitorFetchAllPostsWithTitleQueryFetchMore({
      variables: {
        limit: POSTS_PER_PAGE,
        skip: allPost.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return Object.assign({}, prev, {
          blogsCollection: {
            items: [
              ...(prev.blogsCollection?.items ?? []),
              ...(fetchMoreResult.blogsCollection?.items ?? []),
            ],
          },
        })
      },
    })
  }
  const handleRouterPush = (path: string) => {
    push({ pathname: path }, undefined, {
      locale,
    })
  }

  return (
    <Page
      title={translate('common.blog')}
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: translate('common.blog'),
        url: `https://${host}/${locale}/posts`,
        '@id': `https://${host}/${locale}/posts`,
        publisher: {
          '@type': 'Organization',
          name: rootTitle ?? '',
          logo: {
            '@type': 'ImageObject',
            url: logo ?? '',
          },
        },
        blogPosts: firstViewPosts.map((post) => ({
          '@type': 'BlogPosting',
          mainEntityOfPage: `https://${host}/${locale}/post/${post.title}`,
          headline: post.title,
          author: post.author.name,
          datePublished: post.publishedAt ?? '',
          image: {
            '@type': 'ImageObject',
            url: (post.previewUrl || post.coverUrl) ?? '',
          },
        })),
      }}
    >
      <RootStyle>
        <Container maxWidth={themeStretch ? false : 'lg'}>
          <Stack
            mb={5}
            direction="row"
            alignItems="center"
            justifyContent={{ xs: 'center', md: 'start' }}
            gap={2}
            flexWrap="wrap"
          >
            {allCategories.map((category) => (
              <Button
                key={category}
                variant="outlined"
                sx={{ color: 'gray' }}
                onClick={() => handleRouterPush(`${PATH_PAGE.blogCategory}/${category}`)}
              >
                <Typography variant="h5" textAlign="center">
                  {category}
                </Typography>
              </Button>
            ))}
          </Stack>

          <InfiniteScroll
            dataLength={allPost.length}
            next={handleFetchMore}
            hasMore={allPost.length < totalPosts}
            loader={
              <Stack my={2} alignItems="center" justifyContent="center">
                <CircularProgress color="primary" />
              </Stack>
            }
            endMessage={
              totalPosts > firstViewPosts.length ? (
                <Typography variant="h5" textAlign="center" sx={{ my: 2 }}>
                  Yay! You have seen it all
                </Typography>
              ) : undefined
            }
          >
            <Grid container spacing={3} pb={2}>
              {(!allPost.length ? [...Array(POSTS_PER_PAGE)] : allPost).map((post, index) =>
                post ? (
                  <Grid key={post.id} item xs={12} sm={6} md={(index === 0 && 6) || 3}>
                    <BlogPostCard post={post} index={index} />
                  </Grid>
                ) : (
                  <SkeletonPostItem key={index} />
                )
              )}
            </Grid>
          </InfiniteScroll>
        </Container>
      </RootStyle>
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<PathProps> = async () => {
  const { getPostsPaths } = pathService()
  const paths = APPS.flatMap((app) => getPostsPaths(app.id))

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps: GetStaticProps<PostsPageProps, PathProps> = async ({
  preview = false,
  params,
  locale,
}) => {
  if (!params) throw new Error('No path parameters found')
  const { appId } = params
  const { getAllPostsWithTitle, getAllCategoriesWithPost, getAllPostsTotal } = postService(appId)
  const { getGlobalSetting } = settingService(appId)
  const globalSetting = await getGlobalSetting({ locale })

  const firstViewPosts = (await getAllPostsWithTitle({ limit: POSTS_PER_PAGE, locale })) ?? []

  const allCategories = await getAllCategoriesWithPost({ locale })
  const totalPosts = await getAllPostsTotal({ locale })

  return {
    props: { preview, firstViewPosts, allCategories, totalPosts, globalSetting },
    revalidate: 3600,
  }
}
